import { useRouter } from 'expo-router'
import { useCallback, useEffect, useState, type ReactNode } from 'react'

import { captureException } from '@/src/config/sentry'
import { PrayScreen } from '@/src/features/pray/ui/screens/PrayScreen'
import { useUserQuery } from '@/src/features/user/services/useUserQuery'
import { useToast } from '@/src/general/components/ToastContext'
import { TEST_ID } from '@/src/general/constants/testId'
import { ErrorScreen } from '@/src/general/ui/screens/ErrorScreen'
import { TRACKING_SOURCE } from '@/src/general/utils/gtm/constants/TrackingSource.enum'
import { useRoutes } from '@/src/general/utils/routes'
import { useTranslate } from '@/src/lang/useTranslate'

import { useConversationsPreviewQuery } from '../../services/useConversationsPreviewQuery'
import { useCreatePrayMessageMutation } from '../../services/useCreatePrayMessageMutation'

type TUserPrayScreenProps = {
  userId: number
  conversationId: number | undefined
}

export const UserPrayScreen = ({ userId, conversationId }: TUserPrayScreenProps): ReactNode => {
  const [hasUserClickedAmen, setHasUserClickedAmen] = useState(false)
  const { t } = useTranslate()
  const { addToast } = useToast()
  const { back, navigate, replace } = useRouter()
  const { routes } = useRoutes()

  const createPrayMessage = useCreatePrayMessageMutation({ userId })
  const { data: contact, error, isPending } = useUserQuery(userId)
  const { addInConversationPreview } = useConversationsPreviewQuery()

  const handlePray = () => {
    if (!createPrayMessage.isPending) {
      createPrayMessage
        .mutateAsync()
        .then((message) => {
          if (!contact || conversationId) return
          addInConversationPreview(message, contact)
        })
        .catch((err) => {
          captureException.then((f) => f(err))
          addToast({ colors: 'danger', message: t('common.error.unknown') })
        })
    }
  }

  const newConversationId = createPrayMessage.data?.conversationId
  const isCreatingConversation = !conversationId && !newConversationId && createPrayMessage.isPending

  const goBackToConversation = useCallback(() => {
    setHasUserClickedAmen(true)

    if (conversationId) return back()
    if (newConversationId) return replace(routes.conversationById(newConversationId))

    if (isCreatingConversation) return

    if (userId) return navigate(routes.newConversation(userId))

    return back()
  }, [conversationId, navigate, replace, routes, newConversationId, isCreatingConversation, userId, back])

  useEffect(() => {
    if (!hasUserClickedAmen) return
    if (newConversationId) return replace(routes.conversationById(newConversationId))
  }, [replace, newConversationId, routes, hasUserClickedAmen])

  if (error || (!contact && !isPending))
    return <ErrorScreen error={error} defaultErrorKey="contact.load.error" testID={TEST_ID.PRAY.SCREEN.ERROR} />

  return (
    <PrayScreen
      testID={TEST_ID.PRAY.SCREEN.NAME}
      trackingSource={TRACKING_SOURCE.CONVERSATION}
      onPray={handlePray}
      onAmen={goBackToConversation}
      onClose={goBackToConversation}
      title={contact && t('pray.comment.title', { userName: contact.name })}
      isLoading={!contact}
    />
  )
}
